import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import jwt_decode from 'jwt-decode';
import { isJwtExpired } from 'jwt-check-expiration';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Toaster } from 'react-hot-toast';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
// import Typography from '@material-ui/core/Typography';

import HeadText from '../ui/resetPassword/HeadText';
import CustomButton from '../ui/viewEventUi/CustomButton';
import BodyContainer from '../ui/BodyContainer';
import Services from '../services/services';
import resetPasswordStyles from '../styles/resetPasswordStyles';
import TextFieldComp from '../ui/TextFieldComp';
import GridCenterItem from '../ui/GridCenterItem';
import CustomDialog from '../ui/customDialog';

import {
  setUserBrowserStorage,
  getUserBrowserStorage,
  clearAllBrowserStorage,
  removeSessionItem,
  checkPassword,
  passwordHelper,
} from '../constants';

import {
  showLoadToaster,
  showSuccToaster,
  showErrToaster,
} from '../utils/toasterFunctions';

const useStyles = makeStyles((theme) => resetPasswordStyles(theme));

function ResetPassword() {
  const classes = useStyles();
  const history = useHistory();
  let { pageType, token } = useParams();
  const [userId, setUserId] = useState(0);
  const [userDetail, setUserDetail] = useState({
    fullname: '',
    email: '',
  });
  const [nPass, setNPass] = useState('');
  const [cPass, setCPass] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isLinkExpired, setIsLinkExpired] = useState(false);

  const userDetails = JSON.parse(getUserBrowserStorage('userDetails'));

  const onClickSignIn = () => {
    clearAllBrowserStorage();
    removeSessionItem('prevBoxSeatDetails');
    setDialogOpen(false);
    history.push('/');
    return;
  };

  const logoutFunc = async () => {
    try {
      const res = await Services.logout(userDetails?.userId);
      if (res.data.responseCode === 200) {
        clearAllBrowserStorage();
        removeSessionItem('prevBoxSeatDetails');
      } else {
        showErrToaster(res.data.message);
      }
    } catch (error) {
      showErrToaster('Unknown error!');
    }
  };

  const getUserDetails = async () => {
    try {
      const res = await Services.view_user(userId);
      const details = res.data.userDetails[0];
      setUserDetail({
        fullname:
          details.firstName + (details.lastName ? ` ${details.lastName}` : ''),
        email: details.email,
      });
    } catch (error) {
      showErrToaster('Unknown error!');
    }
  };

  const onChangePass = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    setErrors({});
    if (name === 'nPass') {
      setNPass(value);
    } else if (name === 'cPass') {
      setCPass(value);
    }
  };

  const validationFunc = () => {
    const errors = {};
    if (nPass === '') {
      errors.nPass = 'New password is required';
    } else if (!checkPassword(nPass)) {
      errors.nPass = 'Password must be at least 9 characters';
    }
    if (cPass === '') {
      errors.cPass = 'Confirm your password';
    } else if (nPass !== cPass) {
      errors.cPass = 'Passwords are not matching';
    }
    setErrors(errors);
    const errorStatus = Object.keys(errors).length === 0;
    return errorStatus;
  };

  const submitFunc = async () => {
    if (validationFunc()) {
      const toastId = showLoadToaster('Changing your password..');
      setLoading(true);
      try {
        const res = await Services.update_password(
          nPass,
          userDetail.email,
          cPass
        );

        if (res.data.responseCode === 200) {
          showSuccToaster(res.data.message, toastId);
          setDialogOpen(true);
        } else {
          showErrToaster(res.data.message, toastId);
        }
        setLoading(false);
      } catch (error) {
        showErrToaster('An unknown error occured', toastId);
        setLoading(false);
      }
    }
    return null;
  };

  const handleEnter = (event) => {
    if (event.keyCode === 13) {
      event.preventDefault();
      submitFunc();
    }
  };

  useEffect(() => {
    userDetails?.userId && logoutFunc();
    return () => {};
  }, [userDetails?.userId]);

  useEffect(() => {
    try {
      const isExpired = isJwtExpired(token);
      // setIsLinkExpired
      if (isExpired) {
        setIsLinkExpired(isExpired);
        setDialogOpen(true);
        return;
      } else {
        const decoded = jwt_decode(token);
        const tokens = {
          accessToken: token,
          refreshToken: token,
        };
        setUserBrowserStorage('togetherTokens', JSON.stringify(tokens));
        setUserId(decoded.user_id);
      }
    } catch (error) {
      //invalid token specified //error.message
      showErrToaster(error.message);
    }
  }, []);

  useEffect(() => {
    if (userId) {
      getUserDetails();
    }
  }, [userId]);

  return (
    <BodyContainer
      title={pageType === 'reset' ? 'Reset Password' : 'Set Password'}
    >
      <Grid className={classes.gridOccupy}>
        <Paper elevation={0} className={classes.root}>
          <HeadText
            headText={`Let's ${pageType} your password`}
            captionText={`Hi ${userDetail.fullname}, please set your new password.`}
            emailTextContainer={classes.emailTextContainer}
          />
          <div className={classes.horizontalLine} />
          <Grid container>
            <GridCenterItem>
              <TextFieldComp
                name='nPass'
                label='New Password *'
                placeholder='New Password'
                value={nPass}
                onChange={onChangePass}
                error={!!errors?.nPass}
                helpertext={checkPassword(nPass) ? ' ' : passwordHelper}
                handleEnter={handleEnter}
                inputStyles={classes.inputStyles}
                type='password'
              />
            </GridCenterItem>
            <GridCenterItem>
              <TextFieldComp
                name='cPass'
                label='Confirm Password *'
                placeholder='Confirm Password'
                value={cPass}
                onChange={onChangePass}
                error={!!errors?.cPass}
                helpertext={errors?.cPass}
                handleEnter={handleEnter}
                inputStyles={classes.inputStyles}
                type='password'
              />
            </GridCenterItem>
          </Grid>
          <CustomButton
            btnStyle={classes.submitBtn}
            clickHandle={submitFunc}
            btnText={'Save'}
            disabled={!!loading}
          />
          <CustomDialog
            open={dialogOpen}
            handleClose={() => {
              // setDialogOpen(false);
              // history.push('/');
            }}
            popOverContainer={classes.popOverContainer}
            titleText={
              isLinkExpired
                ? 'Link Expired!'
                : pageType === 'reset'
                ? 'Password Reset'
                : 'Password Set'
            }
            contentText={
              isLinkExpired
                ? 'This link expired! Please click on Forgot Password in Sign In page to get a new link.'
                : 'You have successfully set a new password for login'
            }
            btnOkCls={classes.btnStyleJoin}
            handleOk={onClickSignIn}
            btnOkText={'Sign In'}
            arrowIconCls={classes.arrowIcon}
          />
        </Paper>
      </Grid>
      <Toaster position='top-center'></Toaster>
    </BodyContainer>
  );
}

export default ResetPassword;
